const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

async function request(path, options = {}) {
  const token = localStorage.getItem("viewsday_token");
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (res.status === 204) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const api = {
  // Auth
  register: (data) => request("/auth/register", { method: "POST", body: JSON.stringify(data) }),
  login: (data) => request("/auth/login", { method: "POST", body: JSON.stringify(data) }),
  verifyEmail: (token) => request(`/auth/verify-email?token=${encodeURIComponent(token)}`, { method: "POST" }),
  resendVerification: (email, password) => request("/auth/resend-verification", { method: "POST", body: JSON.stringify({ email, password }) }),
  forgotPassword: (email) => request("/auth/forgot-password", { method: "POST", body: JSON.stringify({ email }) }),
  resetPassword: (token, new_password) => request("/auth/reset-password", { method: "POST", body: JSON.stringify({ token, new_password }) }),

  // Users
  getMe: () => request("/users/me"),
  updateMe: (data) => request("/users/me", { method: "PATCH", body: JSON.stringify(data) }),
  deleteMe: () => request("/users/me", { method: "DELETE" }),

  // Channels
  subscribe: (urls, categories) => request("/channels/subscribe", { method: "POST", body: JSON.stringify({ channels: urls.map(url => ({ url, category: categories[url] || "other" })) }) }),
  listSubscriptions: () => request("/channels/subscriptions"),
  unsubscribe: (channelId) => request(`/channels/subscriptions/${channelId}`, { method: "DELETE" }),

  // Presets
  getPresetNames: () => request("/channels/preset_names"),
  listPresetSubscriptions: () => request("/channels/preset_subscriptions"),
  subscribePreset: (presetId) => request("/channels/preset_subscribe", { method: "POST", body: JSON.stringify({ preset_id: presetId }) }),
  unsubscribePreset: (presetId) => request(`/channels/preset_unsubscribe/${presetId}`, { method: "DELETE" }),

  // Admin
  listFailedDeliveries: () => request("/admin/deliveries/failed"),
  retryDelivery: (userId, videoId) => request(`/admin/deliveries/${userId}/${videoId}/retry`, { method: "POST" }),
  listPresets: () => request("/admin/presets"),
  createPreset: (name, description, category) => request("/admin/presets", { method: "POST", body: JSON.stringify({ name, description, category }) }),
  listAffiliates: () => request("/admin/affiliates"),
  createAffiliate: (userId, code) => request(`/admin/affiliates?user_id=${userId}&code=${encodeURIComponent(code)}`, { method: "POST" }),
  getCommissions: (affiliateId) => request(`/admin/affiliates/${affiliateId}/commissions`),
  getWhatsAppMessages: ({ phone, direction, page, page_size } = {}) => {
    const params = new URLSearchParams();
    if (phone) params.set("phone", phone);
    if (direction) params.set("direction", direction);
    if (page) params.set("page", String(page));
    if (page_size) params.set("page_size", String(page_size));
    return request(`/admin/whatsapp/messages?${params.toString()}`);
  },
  listAdminChannels: () => request("/admin/list_channels"),
  getPresetChannels: (presetId) => request(`/admin/presets/${presetId}`),
  addChannelToPreset: (presetId, channelId) => request(`/admin/presets/${presetId}/channels?channel_id=${channelId}`, { method: "POST", body: JSON.stringify({}) }),
  removeChannelFromPreset: (presetId, channelId) => request(`/admin/presets/${presetId}/channels/${channelId}`, { method: "DELETE" }),

  // Admin Tasks
  triggerPollChannels: () => request("/admin/tasks/poll-channels", { method: "POST" }),
  triggerRetryFailedDeliveries: () => request("/admin/tasks/retry-failed-deliveries", { method: "POST" }),
  triggerBuildDigests: (window) => request(`/admin/tasks/build-digests?window=${encodeURIComponent(window)}`, { method: "POST" }),
  triggerSendDigests: () => request("/admin/tasks/send-digests", { method: "POST" }),
  triggerCleanupOldData: () => request("/admin/tasks/cleanup-old-data", { method: "POST" }),

  // Integrations
  listIntegrations: () => request("/integrations"),
  authorizeNotion: () => request("/integrations/notion/authorize"),
  disconnectIntegration: (service) => request(`/integrations/${service}`, { method: "DELETE" }),
  listNotionDatabases: () => request("/integrations/notion/databases"),
  selectNotionDatabase: (databaseId, databaseName) => request("/integrations/notion/database", { method: "PUT", body: JSON.stringify({ database_id: databaseId, database_name: databaseName }) }),

  // Platforms
  authorizePlatform: (platform) => request(`/integrations/platforms/${platform}/authorize`),
  getPlatformStatus: (platform) => request(`/integrations/platforms/${platform}/status`),
  disconnectPlatform: (platform) => request(`/integrations/platforms/${platform}`, { method: "DELETE" }),

  // Onboarding
  getFeaturedChannels: () => request("/onboarding/suggestions"),
  completeOnboarding: () => request("/users/me/onboarding", { method: "POST" }),

  // Subscriptions
  createCheckout: (plan) => request("/subscriptions/checkout", { method: "POST", body: JSON.stringify({ plan }) }),
  getSubscription: () => request("/subscriptions/me"),
  cancelSubscription: () => request("/subscriptions/cancel", { method: "POST" }),

  // Contact
  sendContact: (email, message) => request("/support/contact", { method: "POST", body: JSON.stringify({ email, message }) }),
};
